
import { useState } from 'react';
import { OnboardingStep } from './useOnboardingState';
import { supabase } from "@/integrations/supabase/client";
import { getStoredOtp, clearStoredOtp } from '@/utils/otp-utils';

interface OtpVerifierProps {
  email: string;
  name: string;
  setStep: (step: OnboardingStep) => void;
  loadUserData: () => void;
}

export const useOtpVerifier = ({
  email,
  name,
  setStep,
  loadUserData
}: OtpVerifierProps) => {
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Verify the OTP and complete user verification
  const handleOtpComplete = async (otp: string): Promise<boolean> => {
    console.log("=== OTP VERIFICATION STARTED ===");
    console.log("User entered OTP:", otp);
    console.log("User entered OTP type:", typeof otp);
    console.log("User entered OTP length:", otp.length);
    
    // Basic validation
    if (!otp || otp.length !== 6) {
      console.log("OTP validation failed: invalid length or empty");
      alert("Veuillez entrer un code de 6 chiffres");
      return false;
    }
    
    setIsVerifying(true);
    
    try {
      // Get the stored OTP to compare
      const { otp: storedOtp, isExpired } = getStoredOtp();
      console.log("Retrieved from storage:");
      console.log("- Stored OTP:", storedOtp);
      console.log("- Stored OTP type:", typeof storedOtp);
      console.log("- Is expired:", isExpired);
      
      if (!storedOtp) {
        console.log("No stored OTP found");
        alert("Veuillez demander un nouveau code de vérification");
        return false;
      }
      
      if (isExpired) {
        console.log("Stored OTP is expired");
        clearStoredOtp();
        alert("Le code a expiré. Veuillez demander un nouveau code");
        return false;
      }
      
      // String comparison with detailed logging
      console.log("=== COMPARING CODES ===");
      console.log("Entered code:", `'${otp}'`);
      console.log("Expected code:", `'${storedOtp}'`);
      console.log("Are they equal (===):", otp === storedOtp);
      console.log("Are they equal (==):", otp == storedOtp);
      
      // Detailed character comparison
      console.log("Character by character comparison:");
      const maxLength = Math.max(otp.length, storedOtp.length);
      for (let i = 0; i < maxLength; i++) {
        const enteredChar = otp[i] || 'undefined';
        const storedChar = storedOtp[i] || 'undefined';
        console.log(`Position ${i}: '${enteredChar}' vs '${storedChar}' - Match: ${enteredChar === storedChar}`);
      }
      
      if (otp !== storedOtp) {
        console.log("OTP codes don't match");
        alert("Le code saisi ne correspond pas. Veuillez vérifier et réessayer");
        return false;
      }
      
      console.log("OTP verified successfully, proceeding with user creation");
      
      // Clear the used OTP immediately
      clearStoredOtp();
      
      // Create or update user in database
      const updateData = {
        name: name.trim(),
        email: email.toLowerCase().trim(),
      };

      console.log("Creating/updating user data:", updateData);
      
      const { data: userData, error: userError } = await supabase
        .from('users')
        .upsert(updateData, { 
          onConflict: 'email',
          ignoreDuplicates: false 
        })
        .select();
        
      if (userError) {
        console.error("Database error:", userError);
        throw new Error("Erreur lors de l'enregistrement. Veuillez réessayer.");
      }

      console.log("User data saved successfully:", userData);

      // Save to localStorage for client-side access
      const localUserData = {
        name: name.trim(),
        email: email.toLowerCase().trim(),
        verified: true,
        onboardingCompleted: true,
        lastVerified: new Date().toISOString()
      };
      
      localStorage.setItem('userData', JSON.stringify(localUserData));
      console.log("User data saved to localStorage:", localUserData);
      
      // Reload user data from localStorage
      loadUserData();
      
      // Move to success step
      setStep('success');
      
      console.log("Vérification réussie - compte configuré avec succès!");
      
      return true;
    } catch (error) {
      console.error("=== OTP VERIFICATION ERROR ===");
      console.error("Error details:", error);
      
      const errorMessage = error instanceof Error ? error.message : "Une erreur s'est produite lors de la vérification";
      
      alert(errorMessage);
      return false;
    } finally {
      setIsVerifying(false);
      console.log("=== OTP VERIFICATION ENDED ===");
    }
  };
  
  return {
    handleOtpComplete,
    isVerifying
  };
};
